import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, Target, TrendingUp, CheckCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate, Link } from "react-router-dom";
import { PageHead } from "@/components/seo/PageHead";

export default function CaseAmazonRevenue() {
  const navigate = useNavigate();
  
  const chartData = [
    { name: 'Création', value: 0 },
    { name: '12 mois', value: 505342.28 },
    { name: '23 mois', value: 704990 }
  ];

  const leviers = [
    "A+ & Store orientés conversion (USP, preuves, comparatifs)",
    "Ads (SP/SB/SD) avec requêtes exactes & négatifs",
    "Pilotage profits (TACOS/ROAS) et disponibilité produit"
  ];

  return (
    <>
      <PageHead
        title="Cas Amazon Chiffre d'Affaires — De 0€ à 705k€ | Cosmos Wave"
        description="Étude de cas Amazon : trajectoire de croissance de 0€ à 704 990€ en 23 mois via A+, Store, Ads et pilotage TACOS/ROAS."
        canonical="https://www.cosmos-wave.com/cases/amazon/chiffre-affaires"
        pageType="article"
      />
      <div className="min-h-screen bg-background">

      {/* Hero Section */}
      <section className="py-12 lg:py-16 xl:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-card/10 via-transparent to-card/10" />
        <div className="container mx-auto px-6 xl:px-8 2xl:px-12 relative">
          <div className="max-w-4xl xl:max-w-5xl mx-auto text-center">
            <Badge variant="outline" className="text-primary border-primary/30 bg-primary/10 mb-6">
              Amazon · Étude 02
            </Badge>
            <h1 className="text-4xl md:text-5xl xl:text-6xl 2xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Trajectoire de chiffre d'affaires
            </h1>
            <p className="text-xl xl:text-2xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
              Création → 12 mois → 23 mois : effet combiné contenu + Ads + ops.
            </p>
            <Button variant="cta-primary" size="xl" onClick={() => navigate("/audit-gratuit")} className="mb-8">
              Demander une présentation
            </Button>
            
            {/* Scroll indicator */}
            <div className="flex flex-col items-center mt-12 lg:mt-16">
              <p className="text-sm text-muted-foreground mb-3">Découvrez l'étude complète</p>
              <div className="w-6 h-10 border-2 border-primary/30 rounded-full flex justify-center">
                <div className="w-1 h-3 bg-primary rounded-full mt-2 animate-bounce"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Values Section */}
      <section className="py-16 bg-card/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              ["Contexte", "CA initial à 0 €. Objectif : traction durable & lisible."],
              ["Interventions clés", "A+ & Store premium, assets packshots/bénéfices, structuration Ads, pilotage TACOS, hygiène catalogue & disponibilité."],
              ["Résultat synthèse", "12 mois : 505 342,28 €. 23 mois : 704 990 € (cumul)."]
            ].map(([key, value], index) => (
              <Card key={key} className="p-6 border-border bg-card backdrop-blur-sm md:col-span-1 first:md:col-span-2">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-primary mb-2">{key}</h3>
                    <p className="text-muted-foreground leading-relaxed">{value}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Chart Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight mb-8 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-primary" />
              Chiffre d'affaires cumulé (€)
            </h2>
            <Card className="p-6 border-border bg-card/50">
              <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="name" 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickFormatter={(value) => `${(value/1000).toFixed(0)}k€`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                      formatter={(value) => [`${value.toLocaleString('fr-FR')} €`, 'CA cumulé']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={3}
                      dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Courbe issue de l'étude fournie (PJ).
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Leviers Section */}
      <section className="py-16 bg-card/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight mb-8 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-primary" />
              Leviers activés
            </h2>
            <div className="grid gap-4">
              {leviers.map((levier, index) => (
                <Card key={index} className="p-4 border-border bg-card/50">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-foreground">{levier}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Note Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 border-primary/20 bg-primary/5 text-center">
              <Target className="w-12 h-12 text-primary mx-auto mb-4" />
              <p className="text-foreground font-medium">
                Données chiffrées présentées sous NDA — détails en rendez-vous.
              </p>
              <Button 
                variant="cta-primary" 
                className="mt-4"
                onClick={() => navigate("/audit-gratuit")}
              >
                Prendre rendez-vous
              </Button>
            </Card>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}